import { APIURL } from "./config";


//offered candidates webcalls
export async function getCandidateInfo(offerId: string, token: string) {
  const res = await fetch(`${APIURL}/OfferedCandidates/offerId/${offerId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.message || "Failed to fetch candidate info");
  }
  const data = await res.json();

  return {
    offerId: data.offerId,
    candidateId: data.candidateId,
    candidateName: data.candidateName,
    candidateEmail: data.candidateEmail,
    role: data.role,
    department: data.department,
    startDate: data.startDate
  };
}

export async function initiateOnboarding(
  offerId: string,
  candidateId: string,
  token: string,
  // Optional official internship period start date (ISO "yyyy-MM-dd"). When supplied it becomes
  // the candidate's fixed commencement date and unlocks the part-time interest step.
  internshipPeriodStartDate?: string | null,
) {
  const params = new URLSearchParams({
    offerId,
    candidateId,
  }).toString();

  const res = await fetch(`${APIURL}/OfferedCandidates/initiate-onboarding?${params}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ internshipPeriodStartDate: internshipPeriodStartDate || null }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.message || "Failed to initiate onboarding");
  }

  return await res.json();
}

export async function submitEmployeeCredentials({
  offerId,
  employeeEmail,
  temporaryPassword,
  token,
}: {
  offerId: string;
  employeeEmail: string;
  temporaryPassword: string;
  token: string;
}) {
  const res = await fetch(`${APIURL}/OfferedCandidates/submit-credentials`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      offerId,
      employeeEmail,
      temporaryPassword,
    }),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.message || "Failed to submit employee credentials");
  }
  return await res.json();
}


// Get a single candidate's status with expanded details for review
export async function getOfferedCandidateDetails(candidateId: string, token: string) {
  const res = await fetch(`${APIURL}/OfferedCandidates/status/${candidateId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch candidate status");
  }
  const data = await res.json();
  return data;

}
export async function getAllOfferedCandidates(token: string) {
  const res = await fetch(`${APIURL}/OfferedCandidates/all`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error("Failed to fetch offered candidates");
  }
  const data = await res.json();
  return data;
}
// Get all offered candidates with their latest step and status
export async function getAllOfferedCandidatesWithLatestStepAndStatus(token: string) {
  const res = await fetch(`${APIURL}/OfferedCandidates/all/latestStatus`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error("Failed to fetch offered candidates with latest step and status");
  }
  const data = await res.json();
  return data;
}

// Get assigned buddies for a candidate by offerId
export async function getAssignedBuddies(offerId: string, token: string) {
  const res = await fetch(`${APIURL}/OfferedCandidates/assignedbuddy/${offerId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch assigned buddies");
  }
  const data = await res.json();
  return data;
}

// Get available contract templates from blob storage
export async function getContractTemplates(token: string) {
  const res = await fetch(`${APIURL}/OfferedCandidates/contract-templates`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch contract templates");
  }
  const data = await res.json();
  return data.templates;
}


// Approve general details (Step 1) — also triggers contract PDF generation using admin's Graph token
export async function approveStep1GeneralDetails(
  submissionId: string,
  adminId: string,
  adminName: string,
  token: string,
  graphToken?: string,
  selectedTemplateName?: string
) {
  const payload = {
    submissionId: submissionId,
    step: "step1",
    graphToken: graphToken ?? null,
    selectedTemplateName: selectedTemplateName ?? null,
  };
  const params = new URLSearchParams({
    adminId: adminId,
    adminName: adminName,
  }).toString();

  const res = await fetch(`${APIURL}/OfferedCandidates/step1/approve?${params}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to approve general details");
  }

  return res.json();
}

//Admin: re-generates and saves the contract PDF for a candidate.
//Call when the automatic generation at Step 1 approval failed.
export async function regenerateContract(
  offerId: string,
  graphToken: string,
  token: string,
  selectedTemplateName?: string
) {
  const res = await fetch(`${APIURL}/OfferedCandidates/${encodeURIComponent(offerId)}/regenerate-contract`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ graphToken, selectedTemplateName: selectedTemplateName ?? null }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to generate contract");
  }

  return res.json();
}

// Admin: re-generate the contract - optionally from a different template - and re-issue it to a
// candidate whose contract signing was NOT approved. Resets Step 2 so the candidate signs the new
// copy and (unless suppressed) emails them about the replacement.
export async function reissueContract(
  offerId: string,
  adminId: string,
  adminName: string,
  token: string,
  options: {
    graphToken: string;
    selectedTemplateName?: string;
    reason?: string;
    notifyCandidate?: boolean;
  }
) {
  const params = new URLSearchParams({
    adminId: adminId,
    adminName: adminName,
  }).toString();

  const res = await fetch(
    `${APIURL}/OfferedCandidates/step2/reissue-contract/${encodeURIComponent(offerId)}?${params}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        graphToken: options.graphToken,
        selectedTemplateName: options.selectedTemplateName ?? null,
        reason: options.reason ?? null,
        notifyCandidate: options.notifyCandidate ?? true,
      }),
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to re-issue contract");
  }

  return res.json();
}
// Reject general details (Step 1)
export async function rejectStep1GeneralDetails(submissionId: string, adminId: string, adminName: string, reason: string, token: string) {
  const payload = {
    submissionId: submissionId,
    step: "step1",
    reason: reason,
  };
  const params = new URLSearchParams({
    adminId: adminId,
    adminName: adminName,
  }).toString();

  const res = await fetch(`${APIURL}/OfferedCandidates/step1/reject?${params}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to reject general details");
  }

  return res.json();
}

// Approve signed contract (Step 2)
export async function approveStep2SignedContract(submissionId: string, adminId: string, adminName: string, token: string) {
  const payload = {
    submissionId: submissionId,
    step: "step2",
  };
  const params = new URLSearchParams({
    adminId: adminId,
    adminName: adminName,
  }).toString();

  const res = await fetch(`${APIURL}/OfferedCandidates/step2/approve?${params}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to approve signed contract");
  }

  return res.json();
}
// Reject signed contract (Step 2)
export async function rejectStep2SignedContract(
  submissionId: string,
  adminId: string,
  adminName: string,
  reason: string,
  token: string
) {
  const payload = {
    submissionId: submissionId,
    reason: reason,
  };
  const params = new URLSearchParams({
    adminId: adminId,
    adminName: adminName,
  }).toString();

  const res = await fetch(`${APIURL}/OfferedCandidates/step2/reject?${params}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to reject signed contract");
  }

  return res.json();
}

// Approve welcome pack (Step 3)
export async function approveStep3WelcomePack(
  submissionId: string,
  adminId: string,
  adminName: string,
  token: string
) {
  const payload = {
    submissionId: submissionId,
    step: "step3"
  };
  const params = new URLSearchParams({
    adminId: adminId,
    adminName: adminName,
  }).toString();

  const res = await fetch(`${APIURL}/OfferedCandidates/step3/approve?${params}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to approve welcome pack");
  }

  return res.json();
}
// Reject welcome pack (Step 3)
export async function rejectStep3WelcomePack(
  submissionId: string,
  adminId: string,
  adminName: string,
  reason: string,
  token: string
) {
  const payload = {
    submissionId: submissionId,
    reason: reason,
  };
  const params = new URLSearchParams({
    adminId: adminId,
    adminName: adminName,
  }).toString();

  const res = await fetch(`${APIURL}/OfferedCandidates/step3/reject?${params}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to reject welcome pack");
  }

  return res.json();
}
//  signed contract PDF uploaded by candidate, for a given offerId
export async function downloadSignedContractByCandidate(offerId: string, token: string): Promise<Blob> {
  const url = `${APIURL}/OfferedCandidates/step2/getsignedcontract/${encodeURIComponent(offerId)}`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    let errMsg = "Failed to download signed contract";
    try {
      const err = await res.json();
      errMsg = err.message || errMsg;
    } catch {
      errMsg = await res.text();
    }
    throw new Error(errMsg);
  }

  const blob = await res.blob();
  return blob;
}

// Get generated unsigned contract PDF
export async function getGeneratedContractPdf(offerId: string, token: string): Promise<Blob> {
  const url = `${APIURL}/OfferedCandidates/step2/getgeneratedcontract/${encodeURIComponent(offerId)}`;
  // console.log("Fetching generated contract from:", url);
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  // console.log("Response status:", res.status, res.statusText);

  if (!res.ok) {
    let errMsg = "Failed to download generated contract";
    try {
      const err = await res.json();
      errMsg = err.message || errMsg;
      console.error("API error response:", err);
    } catch {
      errMsg = await res.text();
      console.error("API error text:", errMsg);
    }
    throw new Error(errMsg);
  }

  const blob = await res.blob();
  // console.log("Successfully retrieved PDF blob, size:", blob.size);
  return blob;
}

//get employee from graph api
export async function getEmployeesFromGraphApi(token: string) {
  const res = await fetch(`${APIURL}/OfferedCandidates/GetEmployeesfromGraphApi`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to fetch employees from Graph API");
  }

  return res.json();
}

//API call for assigning onboarding buddy
export async function assignBuddies(data: {
  offerId: string;
  buddies: { id: string; name: string | null; email: string | null }[];
  meetingDate: Date;
}, token: string) {

  // Set the time to 11:00 AM for the meeting date
  const meetingDateTime = new Date(data.meetingDate);
  meetingDateTime.setHours(11, 0, 0, 0);// default temp

  const payload = {
    offerId: data.offerId,
    MeetingDateTime: meetingDateTime,
    buddies: data.buddies.map(b => ({
      id: b.id,
      name: b.name,
      email: b.email,
    })),
  };

  // console.log(payload)
  const res = await fetch(`${APIURL}/OfferedCandidates/assign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to assign buddies");
  }
  return await res.json();

}


export async function searchOfferedCandidates(params: {
  search?: string;
  status?: string;
  step?: string;
  page?: number;
  pageSize?: number;
}, token: string) {
  const queryParams = new URLSearchParams();

  if (params.search) queryParams.append("search", params.search);
  if (params.status) queryParams.append("status", params.status);
  if (params.step) queryParams.append("step", params.step);
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.pageSize) queryParams.append("pageSize", params.pageSize.toString());

  const res = await fetch(
    `${APIURL}/OfferedCandidates/search?${queryParams.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!res.ok) throw new Error("Failed to search candidates");
  return res.json();
}

export async function markOnboardingComplete(
  offerId: string,
  adminId: string,
  adminName: string,
  token: string,
  msEmail?: string
) {
  const params = new URLSearchParams({
    adminId: adminId,
    adminName: adminName,
  }).toString();

  const res = await fetch(`${APIURL}/OfferedCandidates/mark-complete/${offerId}?${params}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(msEmail ? { msEmail } : {}),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const errorObj = new Error(err.message || "Failed to mark onboarding as complete") as any;
    errorObj.requiresMsEmail = err.requiresMsEmail;
    throw errorObj;
  }

  return res.json();
}

// Send calendar invite to candidate and buddies
export async function sendCalendarInvite(data: {
  offerId: string;
  candidateEmail: string;
  candidateName: string;
  buddyEmails: string[];
  startDateTime: Date;
  endDateTime: Date;
  subject: string;
  body: string;
}, token: string) {
  const payload = {
    offerId: data.offerId,
    candidateEmail: data.candidateEmail,
    candidateName: data.candidateName,
    buddyEmails: data.buddyEmails,
    startDateTime: data.startDateTime.toISOString(),
    endDateTime: data.endDateTime.toISOString(),
    subject: data.subject,
    body: data.body,
  };

  const res = await fetch(`${APIURL}/OfferedCandidates/send-calendar-invite`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to send calendar invite");
  }

  return res.json();
}
